<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { computed, ref } from 'vue'
import type { Nullable } from '@/types/shared/common'
import BaseButton from '@/components/base/BaseButton.vue'
import CrossCircleIcon2 from '@/components/icons/CrossCircleIcon2.vue'
import ShopItemColumnBanner from '@/components/modals/ShopItemColumnBanner.vue'
import ShopItemDetailsModal from '@/components/modals/ShopItemDetailsModal.vue'
import ShopPurchaseSuccessModal from '@/components/modals/ShopPurchaseSuccessModal.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { useConfetti } from '@/composables/game/confetti'

const props = withDefaults(defineProps<{
	show: boolean,
	currency: string
	ecommerceItems: EcommerceItem[]
	subtitle?: string
	hasCheckmark?: boolean
}>(), {
  subtitle: undefined,
  hasCheckmark: true,
})

const emit = defineEmits<{
	(e: 'hide', hideForToday: boolean): void
}>()

const { showConfetti } = useConfetti()

const selectedItem = ref<Nullable<EcommerceItem>>(null)
const hideForToday = ref(false)
const showPurchaseModal = ref(false)
const showSuccessModal = ref(false)
const purchaseDone = ref(false)

type SpecialCurrencyBenefit = { type: ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY; currency: string; amount: number }

function getSpecialCurrencyBenefit(item: EcommerceItem): SpecialCurrencyBenefit | undefined {
  return item.benefits.find(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) as SpecialCurrencyBenefit | undefined
}

function getCurrencyImage(benefit: SpecialCurrencyBenefit | undefined): string | undefined {
  return benefit ? allSpecialCurrenciesConfig.value[benefit.currency]?.image : undefined
}

const sortedEcommerceItems = computed(() => {
  return [...props.ecommerceItems]
    .filter(item => item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY))
    .sort((a, b) => (a.prices?.[0]?.amount ?? 0) - (b.prices?.[0]?.amount ?? 0))
    .slice(0, 3)
})

const sortedEcommerceItemBenefits = computed(() =>
  sortedEcommerceItems.value.map(item => getSpecialCurrencyBenefit(item)),
)

const { getStarPrice } = useEcommercePrice()
const sortedEcommerceItemPrices = computed(() =>
  sortedEcommerceItems.value.map(item => getStarPrice(item)),
)

function handlePurchase() {
  showPurchaseModal.value = false
  purchaseDone.value = true
  showConfetti()
  showSuccessModal.value = true
}

function handleSuccessClose() {
  showSuccessModal.value = false
  purchaseDone.value = false
  emit('hide', false)
}
</script>

<template>
  <TheBottomSheet
    :show="show && !purchaseDone"
    centered
    bg-fade-class="!z-[804]"
    wrapper-class="!z-[804]"
    close-button-class="z-[10] absolute -top-1.5 -right-1.5"
    outer-content-class="overflow-visible rounded-t-[24px] rounded-b-[24px] bg-transparent"
    border-container-class="overflow-visible rounded-t-[24px] rounded-b-[24px]"
    base-modal-class="overflow-visible"
    content-class="py-8 [background-image:url('@/assets/images/modal-bg-out-of-currency.webp')] rounded-t-[22px] rounded-b-[22px]"
    @hide="!purchaseDone && emit('hide', $event ? hideForToday : false)"
  >
    <template #closeButton>
      <CrossCircleIcon2 class="limited-pack-close-icon size-8" />
    </template>
    <div
      class="absolute inset-1 rounded-[22px] bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.02)_80%,rgba(0,0,0,0)_100%)] z-[1]"
    />
    <div class="flex w-full flex-col items-center relative z-[2]">
      <div
        class="px-4 text-shadow-2 font-pilat text-center text-[48px] leading-[42px] font-black text-light-white"
      >
        Out of <span class="lowercase">{{ allSpecialCurrenciesConfig[currency]?.label }}</span>?
      </div>

      <div class="mt-12 w-full grid grid-cols-3 gap-2 items-end">
        <div class="w-full h-[137px] shop-item-column-wrapper-1">
          <div
            class="absolute  text-[#8F8F8F] text-center text-[13px] font-black leading-[12px] uppercase [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.44))] [-webkit-text-stroke:4px_#1D001D] [paint-order:stroke_fill] whitespace-nowrap z-[3] rotate-[-11deg] left-1/2 -translate-x-[calc(50%+2px)] top-2"
          >
            <span class="bump-value inline-block">Warm UP</span>
          </div>
          <ShopItemColumnBanner
            v-if="sortedEcommerceItems[0]"
            :item="sortedEcommerceItems[0]"
          />
          <div class="shop-item-column pt-6">
            <div class="flex flex-col items-center">
              <img
                v-if="sortedEcommerceItemBenefits[0]"
                :src="getCurrencyImage(sortedEcommerceItemBenefits[0])"
                alt=""
                class="shop-item-currency-image"
              >
              <span class="shop-item-currency-amount">
                {{ sortedEcommerceItemBenefits[0] ? `+${sortedEcommerceItemBenefits[0].amount}` : '' }}
              </span>
            </div>
            <BaseButton
              v-if="sortedEcommerceItemPrices[0]"
              style-type="primary-pink"
              class="mt-auto w-full !h-8 !py-0 !text-base relative z-[1]"
              @click="selectedItem = sortedEcommerceItems[0]; showPurchaseModal = true"
            >
              ${{ sortedEcommerceItemPrices[0].usdAmount }}
            </BaseButton>
          </div>
        </div>
        <div class="w-full h-[182px] shop-item-column-wrapper-1">
          <div
            class="absolute  text-[#14F1FF] text-center text-[15px] font-black leading-[14px] uppercase [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.44))] [-webkit-text-stroke:5px_#1D001D] [paint-order:stroke_fill] z-[3] rotate-[-11deg] left-1/2 -translate-x-1/2 top-0"
          >
            <span class="bump-value inline-block">Push <br> Ahead!</span>
          </div>
          <ShopItemColumnBanner
            v-if="sortedEcommerceItems[1]"
            :item="sortedEcommerceItems[1]"
          />
          <div class="shop-item-column pt-[52px]">
            <div class="flex flex-col items-center">
              <img
                v-if="sortedEcommerceItemBenefits[1]"
                :src="getCurrencyImage(sortedEcommerceItemBenefits[1])"
                alt=""
                class="shop-item-currency-image"
              >
              <span class="shop-item-currency-amount">
                {{ sortedEcommerceItemBenefits[1] ? `+${sortedEcommerceItemBenefits[1].amount}` : '' }}
              </span>
            </div>
            <BaseButton
              v-if="sortedEcommerceItemPrices[1]"
              style-type="primary-pink"
              class="mt-auto w-full !h-8 !py-0 !text-base relative z-[1]"
              @click="selectedItem = sortedEcommerceItems[1]; showPurchaseModal = true"
            >
              ${{ sortedEcommerceItemPrices[1].usdAmount }}
            </BaseButton>
          </div>
        </div>
        <div class="w-full h-[216px] shop-item-column-wrapper-1">
          <div
            class="absolute  text-[#F00] text-center text-[20px] font-black leading-[17px] uppercase [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.44))] [-webkit-text-stroke:6px_#1D001D] [paint-order:stroke_fill] whitespace-nowrap z-[3] rotate-[-11deg] left-1/2 -translate-x-1/2 top-1"
          >
            <span class="bump-value inline-block">BEAST <br> MODE</span>
          </div>
          <ShopItemColumnBanner
            v-if="sortedEcommerceItems[2]"
            :item="sortedEcommerceItems[2]"
          />
          <div class="shop-item-column pt-[70px]">
            <div class="flex flex-col items-center">
              <img
                v-if="sortedEcommerceItemBenefits[2]"
                :src="getCurrencyImage(sortedEcommerceItemBenefits[2])"
                alt=""
                class="shop-item-currency-image"
              >
              <span class="shop-item-currency-amount">
                {{ sortedEcommerceItemBenefits[2] ? `+${sortedEcommerceItemBenefits[2].amount}` : '' }}
              </span>
            </div>
            <BaseButton
              v-if="sortedEcommerceItemPrices[2]"
              style-type="primary-pink"
              class="mt-auto w-full !h-8 !py-0 !text-base relative z-[1]"
              @click="selectedItem = sortedEcommerceItems[2]; showPurchaseModal = true"
            >
              ${{ sortedEcommerceItemPrices[2].usdAmount }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </TheBottomSheet>

  <ShopItemDetailsModal
    v-if="selectedItem && showPurchaseModal"
    :item="selectedItem"
    :show="showPurchaseModal"
    bg-fade-class="!z-[805]"
    wrapper-class="!z-[805]"
    @hide="showPurchaseModal = false"
    @on-purchase="handlePurchase"
  >
    <template #itemIcon>
      <div class="flex flex-col items-center">
        <img
          :src="getCurrencyImage(getSpecialCurrencyBenefit(selectedItem))"
          class="w-[135px] h-auto filter drop-shadow-[0_0_24px_rgba(0,0,0,0.62)]"
          alt=""
        >
        <span
          class="text-center uppercase font-bold text-[#671DFB] text-[69px] leading-[93px] [-webkit-text-stroke-width:14px] [-webkit-text-stroke-color:#36EEFF] [paint-order:stroke_fill] -mt-10 [filter:drop-shadow(0_0_18px_#10F)]"
        >
          {{ `+${getSpecialCurrencyBenefit(selectedItem)?.amount}` }}
        </span>
      </div>
    </template>
  </ShopItemDetailsModal>

  <ShopPurchaseSuccessModal
    v-if="selectedItem && showSuccessModal"
    :item="selectedItem"
    :show="showSuccessModal"
    bg-fade-class="!z-[805]"
    wrapper-class="!z-[805]"
    @hide="handleSuccessClose"
  >
    <template #itemIcon>
      <div class="flex flex-col items-center">
        <img
          :src="getCurrencyImage(getSpecialCurrencyBenefit(selectedItem))"
          class="w-[135px] h-auto filter drop-shadow-[0_0_24px_rgba(0,0,0,0.62)]"
          alt=""
        >
        <span
          class="text-center uppercase font-bold text-[#671DFB] text-[69px] leading-[93px] [-webkit-text-stroke-width:14px] [-webkit-text-stroke-color:#36EEFF] [paint-order:stroke_fill] -mt-10 [filter:drop-shadow(0_0_18px_#10F)]"
        >
          {{ `+${getSpecialCurrencyBenefit(selectedItem)?.amount}` }}
        </span>
      </div>
    </template>
  </ShopPurchaseSuccessModal>
</template>

<style scoped>
.shop-item-column-wrapper-1 {
	@apply rounded-[28px] relative flex flex-col;
	background: linear-gradient(0deg, rgba(255, 94, 0, 0.44) 0.15%, rgba(255, 0, 217, 0.44) 100.08%);
	backdrop-filter: blur(20px);
}

.shop-item-column-wrapper-1::after {
	content: '';
	position: absolute;
	inset: 0;
	border-radius: 28px;
	padding: 1px;
	background: linear-gradient(126deg, #75201F 0%, rgba(153, 153, 153, 0.32) 100%);
	-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
	-webkit-mask-composite: xor;
	mask-composite: exclude;
	pointer-events: none;
	z-index: 1;
}

.shop-item-currency-image {
	@apply w-[42px] h-auto drop-shadow-[0_0_24px_rgba(0,0,0,0.62)];
}

.shop-item-currency-amount {
	@apply text-center uppercase font-bold text-[#671DFB] text-[21px] leading-[29px] -mt-3;
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #36EEFF;
	paint-order: stroke fill;
	filter: drop-shadow(0 0 10px #10F);
}

.shop-item-column {
	@apply rounded-[27px] px-3 pb-3 flex flex-col items-center w-full flex-1 relative;
	background: linear-gradient(0deg, rgba(255, 94, 0, 0.44) 0.15%, rgba(255, 0, 217, 0.44) 100.08%);
	backdrop-filter: blur(20px);
}

.shop-item-column::before {
	content: '';
	position: absolute;
	inset: 0;
	border-radius: 27px;
	background: rgba(0, 0, 0, 0.4);
	pointer-events: none;
}

@keyframes scaleTwice {
	0% {
		transform: scale(1);
	}
	10% {
		transform: scale(1.1); /* First bump */
	}
	20% {
		transform: scale(1);
	}
	30% {
		transform: scale(1.1); /* Second bump */
	}
	40% {
		transform: scale(1);
	}
	100% {
		transform: scale(1); /* Hold for delay */
	}
}

.bump-value {
	animation: scaleTwice 3s ease-in-out infinite; /* Total duration: 3s */
}
</style>
